/**
 * A month bar's pixel height, against the tallest month in the trailing-12-month window. The bar area
 * is 55px (overview-rebuild-pr5-plan.md's "Bar scaling": the 74px column is 55 bar + 6 gap + 13 label).
 * Two guards, both returning the 3px floor rather than an unusable height: `total === 0` (`0 / 0` is
 * `NaN`, which React renders as no inline height at all) and `maxTotal <= 0` (review fix B2: a
 * non-zero total against a zero-or-negative max is `Infinity` -- not `NaN` -- or a negative scale,
 * neither of which the real caller can ever actually produce, since `maxTotal` is the max of all
 * totals and so a non-zero total forces `maxTotal` non-zero too; but `barHeight` is exported and pure,
 * so its contract holds for any input, not only what today's single caller happens to pass). A zero
 * month is ALSO 3px when `maxTotal > 0` (there's simply nothing to show), so both guards can return
 * the same floor; only the dot/bar COLOUR (decided by the component, not here) tells a real zero month
 * apart from a merely-tiny one at this same 3px height.
 *
 * @param total - this month's total.
 * @param maxTotal - the tallest month's total across the whole 12-month window.
 * @returns a pixel height from 3 to 55.
 */
export const barHeight = (total: number, maxTotal: number): number => {
  if (total === 0 || maxTotal <= 0) return 3;
  return Math.max(3, Math.round((total / maxTotal) * 55));
};

/**
 * A month bar's single-letter label, e.g. `"O"` for October. Built from the month's OWN parts via
 * `Date.UTC` and read back with `{ timeZone: "UTC" }` -- the same UTC-by-construction rule `dayLabel`
 * uses in `givingWeek.ts`, and for the same reason: `month` is a bare `yyyy-MM` calendar month with no
 * instant of its own, so it must never be parsed as local time. Two `M`s (March/May) and repeats among
 * January/June/July are expected -- this returns the first letter only, with no attempt to
 * disambiguate collisions.
 *
 * @param month - a `yyyy-MM` calendar month, as sent by the api.
 * @returns the month name's first letter, e.g. `"O"`.
 */
export const barLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-").map(Number);
  const monthName = new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return monthName.charAt(0);
};

/**
 * A month bar's full month name, e.g. `"October"` -- review fix C3: the bar carries its state (a real
 * zero vs. a merely-tiny month) by colour alone at the shared 3px floor, so a screen reader needs a
 * real accessible name, and the visible one-letter label (`barLabel`) reads worse as a spoken word than
 * the full name does. Same UTC-by-construction rule as `barLabel` above, for the same reason.
 *
 * @param month - a `yyyy-MM` calendar month, as sent by the api.
 * @returns the month name in full, e.g. `"October"`.
 */
export const monthFullLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
};
